import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Props {
  children: React.ReactNode[]
  intervalMs?: number
}

/**
 * Reusable auto-scrolling slider with left/right manual navigation buttons.
 * Pauses auto-scroll on hover. Supports responsive layouts.
 */
export function AutoscrollSlider({ children, intervalMs = 4000 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(true)
  const isHovered = useRef(false)

  useEffect(() => {
    if (children.length <= 1 || !intervalMs) return

    const interval = setInterval(() => {
      if (isHovered.current || !containerRef.current) return

      const el = containerRef.current
      const maxScroll = el.scrollWidth - el.clientWidth

      if (el.scrollLeft >= maxScroll - 15) {
        // Wrap around to start smoothly
        el.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        // Scroll right by approximately one item width (250px)
        el.scrollBy({ left: 260, behavior: 'smooth' })
      }
    }, intervalMs)

    return () => clearInterval(interval)
  }, [children.length, intervalMs])

  const handleScroll = () => {
    if (!containerRef.current) return
    const el = containerRef.current
    setShowLeft(el.scrollLeft > 15)
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 15)
  }

  const scroll = (direction: 'left' | 'right') => {
    if (!containerRef.current) return
    const el = containerRef.current
    const amount = direction === 'left' ? -260 : 260
    el.scrollBy({ left: amount, behavior: 'smooth' })
  }

  if (children.length === 0) return null

  return (
    <div
      className="relative group/slider w-full"
      onMouseEnter={() => {
        isHovered.current = true
      }}
      onMouseLeave={() => {
        isHovered.current = false
      }}
    >
      {/* Left Navigation Arrow */}
      {showLeft && (
        <button
          type="button"
          onClick={() => scroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full border border-[#657086] bg-[#3a4352]/90 text-white shadow-lg hover:bg-[#bc9050] hover:text-[#17120f] transition-all duration-300 md:opacity-0 md:group-hover/slider:opacity-100 flex items-center justify-center"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}

      {/* Slider Scroll Area */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex gap-4 overflow-x-auto pb-3 scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children.map((child, idx) => (
          <div key={idx} className="snap-start shrink-0">
            {child}
          </div>
        ))}
      </div>

      {/* Right Navigation Arrow */}
      {showRight && (
        <button
          type="button"
          onClick={() => scroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full border border-[#657086] bg-[#3a4352]/90 text-white shadow-lg hover:bg-[#bc9050] hover:text-[#17120f] transition-all duration-300 md:opacity-0 md:group-hover/slider:opacity-100 flex items-center justify-center"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
    </div>
  )
}
